import { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { usePodcastKeepalive } from './contexts/PodcastKeepaliveContext.js';
import { AppLayout } from './components/layout/AppLayout';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { SyncPendingBadge } from './components/ui/SyncPendingBadge';
import { Dashboard } from './pages/Dashboard';
import { Visuales } from './pages/Visuales';
const CourseList = lazy(() =>
  import('./pages/CourseList').then((m) => ({ default: m.CourseList })),
);
const CourseBuilder = lazy(() =>
  import('./pages/CourseBuilder').then((m) => ({ default: m.CourseBuilder })),
);
const PublicCourse = lazy(() =>
  import('./pages/PublicCourse').then((m) => ({ default: m.PublicCourse })),
);
import { Badges } from './pages/Badges';
import LibraryBooks from './pages/LibraryBooks';
import { Login } from './pages/Login';
import { AuthCallback } from './pages/AuthCallback';
import { Lock } from 'lucide-react';
import { useAuth } from './hooks/useAuth';
import { auth } from './services/auth';

/**
 * Thin route element for /podcasts/:id.
 * Registers the podcast ID in the keepalive context so PodcastKeepaliveLayer
 * (in main.tsx) renders PodcastStudy in a persistent fixed overlay.
 * Returns null because PodcastStudy is already rendered by the keepalive layer.
 */
function PodcastActivator() {
  const { id } = useParams<{ id: string }>();
  const { setPersisted } = usePodcastKeepalive();
  useEffect(() => {
    if (id) setPersisted(id);
  }, [id, setPersisted]);
  return null;
}

// Heavy pages — loaded only when the route is visited
const CanvasEditor = lazy(() => import('./pages/CanvasEditor'));
const PublicCanvas = lazy(() => import('./pages/PublicCanvas'));
const DiagramNew = lazy(() => import('./pages/DiagramNew'));
const DiagramEditorPage = lazy(() => import('./pages/DiagramEditor'));
const PodcastList = lazy(() =>
  import('./pages/PodcastList').then((m) => ({ default: m.PodcastList })),
);
const PodcastBuilder = lazy(() =>
  import('./pages/PodcastBuilder').then((m) => ({ default: m.PodcastBuilder })),
);
const Progress = lazy(() => import('./pages/Progress').then((m) => ({ default: m.Progress })));
const KnowledgeGraph = lazy(() => import('./pages/KnowledgeGraph'));
const Settings = lazy(() => import('./pages/Settings'));
const ProjectList = lazy(() =>
  import('./pages/ProjectList').then((m) => ({ default: m.ProjectList })),
);
const ProjectWorkspace = lazy(() =>
  import('./pages/ProjectWorkspace').then((m) => ({ default: m.ProjectWorkspace })),
);
const Companies = lazy(() => import('./pages/Companies').then((m) => ({ default: m.Companies })));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-xd-action border-t-transparent" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (!auth.hasVaultAccess()) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-surface gap-4 text-center px-4">
        <Lock className="w-12 h-12 text-xd-error" />
        <h1 className="text-xl font-semibold text-text">Acceso restringido</h1>
        <p className="text-sm text-text-muted max-w-xs">
          Tu cuenta no tiene acceso al Vault. Contacta al administrador para solicitar acceso.
        </p>
        <a href="https://xdface.net" className="mt-2 text-sm text-xd-info hover:underline">
          Volver a xdface.net
        </a>
        <button
          onClick={() => auth.logout()}
          className="text-xs text-text-subtle hover:text-text-muted"
        >
          Cerrar sesión
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

const PageSpinner = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-xd-action border-t-transparent" />
  </div>
);

export function App() {
  return (
    <ErrorBoundary>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/callback" element={<AuthCallback />} />
        <Route
          path="/public/course/:token"
          element={
            <Suspense fallback={<PageSpinner />}>
              <PublicCourse />
            </Suspense>
          }
        />
        <Route
          path="/public/canvas/:token"
          element={
            <Suspense fallback={<PageSpinner />}>
              <PublicCanvas />
            </Suspense>
          }
        />

        {/* Protected routes */}
        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<Dashboard />} />

          {/* Visuales (Pizarras + Diagramas) */}
          <Route path="/canvas" element={<Visuales />} />
          <Route
            path="/canvas/new"
            element={
              <Suspense fallback={<PageSpinner />}>
                <CanvasEditor />
              </Suspense>
            }
          />
          <Route
            path="/canvas/:id"
            element={
              <Suspense fallback={<PageSpinner />}>
                <CanvasEditor />
              </Suspense>
            }
          />
          <Route
            path="/diagrams/new"
            element={
              <Suspense fallback={<PageSpinner />}>
                <DiagramNew />
              </Suspense>
            }
          />
          <Route
            path="/diagrams/:id"
            element={
              <Suspense fallback={<PageSpinner />}>
                <DiagramEditorPage />
              </Suspense>
            }
          />

          {/* Projects */}
          <Route
            path="/projects"
            element={
              <Suspense fallback={<PageSpinner />}>
                <ProjectList />
              </Suspense>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <Suspense fallback={<PageSpinner />}>
                <ProjectWorkspace />
              </Suspense>
            }
          />
          <Route
            path="/companies"
            element={
              <Suspense fallback={<PageSpinner />}>
                <Companies />
              </Suspense>
            }
          />

          {/* Courses */}
          <Route
            path="/courses"
            element={
              <Suspense fallback={<PageSpinner />}>
                <CourseList />
              </Suspense>
            }
          />
          <Route
            path="/courses/new"
            element={
              <Suspense fallback={<PageSpinner />}>
                <CourseBuilder />
              </Suspense>
            }
          />
          <Route
            path="/courses/:id"
            element={
              <Suspense fallback={<PageSpinner />}>
                <CourseBuilder />
              </Suspense>
            }
          />

          {/* Podcasts */}
          <Route
            path="/podcasts"
            element={
              <Suspense fallback={<PageSpinner />}>
                <PodcastList />
              </Suspense>
            }
          />
          <Route
            path="/podcasts/new"
            element={
              <Suspense fallback={<PageSpinner />}>
                <PodcastBuilder />
              </Suspense>
            }
          />
          <Route
            path="/podcasts/:id/edit"
            element={
              <Suspense fallback={<PageSpinner />}>
                <PodcastBuilder />
              </Suspense>
            }
          />
          {/* PodcastStudy is rendered by PodcastKeepaliveLayer in main.tsx.
            PodcastActivator just registers the ID so the layer shows it. */}
          <Route path="/podcasts/:id" element={<PodcastActivator />} />

          {/* Library */}
          <Route path="/library" element={<LibraryBooks />} />

          {/* Progress */}
          <Route
            path="/progress"
            element={
              <Suspense fallback={<PageSpinner />}>
                <Progress />
              </Suspense>
            }
          />
          <Route path="/badges" element={<Badges />} />

          {/* Settings */}
          <Route
            path="/settings"
            element={
              <Suspense fallback={<PageSpinner />}>
                <Settings />
              </Suspense>
            }
          />

          {/* Knowledge Graph */}
          <Route
            path="/graph"
            element={
              <Suspense fallback={<PageSpinner />}>
                <KnowledgeGraph />
              </Suspense>
            }
          />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
      <SyncPendingBadge />
    </ErrorBoundary>
  );
}
